import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { Dropdown } from 'primereact/dropdown';
import { toastify } from '../../../utils/toast';
import { makeRequest2 , makeRequest3} from '../../../utils/makeRequest';
import { waterMeterBaseUrl } from '../../../utils/urls';
import Layout from '../component/layout';

function Meters() {
  const navigate = useNavigate();
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalMeters: 0,
    limit: 10,
  });

  // Configuration states for dropdowns
  const [manufacturers, setManufacturers] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [meterSizes, setMeterSizes] = useState([]);

  // Enhanced Import Dialog States
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // Form data state for editing
  const [formData, setFormData] = useState({
    meterNumber: '',
    manufacturer: '',
    protocol: '',
    size: '',
    initialReading: '',
    concentratorSerialNumber: '',
    installationStatus: 'not installed',
  });

  // Base URLs for configuration endpoints
  const waterMeterSizeBaseUrl = '/api/core/water-meters-sizes';
  const waterMeterProtocolsBaseUrl = '/api/core/water-meter-communcation-protocol';
  const waterMeterManufacturerBaseUrl = '/api/core/water-meter-manufacturer';

  // Rows per page options
  const rowsPerPageOptions = [10, 20, 50, 100];

  // Fetch configuration data
  const fetchConfigurations = async () => {
    try {
      // Fetch manufacturers
      const manufacturersResponse = await makeRequest2(waterMeterManufacturerBaseUrl, 'GET');
      if (manufacturersResponse.success) {
        setManufacturers(manufacturersResponse.data || []);
      }

      // Fetch protocols
      const protocolsResponse = await makeRequest2(waterMeterProtocolsBaseUrl, 'GET');
      if (protocolsResponse.success) {
        setProtocols(protocolsResponse.data || []);
      }

      // Fetch meter sizes
      const sizesResponse = await makeRequest2(waterMeterSizeBaseUrl, 'GET');
      if (sizesResponse.success) {
        setMeterSizes(sizesResponse.data || []);
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  };

  const fetchMeters = async (page = 1, limit = 10, search = '') => {
    try {
      setLoading(true);
      let url = `${waterMeterBaseUrl}?page=${page}&limit=${limit}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await makeRequest2(url, 'GET');
      if (response && response.data) {
        setMeters(response.data.meters || []);
        setPagination({
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalMeters: response.data.pagination.totalMeters,
          limit: response.data.pagination.limit,
        });
      } else {
        toastify(response?.error || 'Failed to fetch meters', 'error');
      }
    } catch (error) {
      toastify('An error occurred while fetching meters', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeters(pagination.currentPage, pagination.limit, searchTerm);
    fetchConfigurations();
  }, []);

  useEffect(() => {
    // Debounce search to avoid too many requests
    const timer = setTimeout(() => {
      fetchMeters(1, pagination.limit, searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Helper function to find manufacturer name by ID or return the name directly
  const getManufacturerName = (value) => {
    const manufacturer = manufacturers.find(m => m._id === value);
    return manufacturer ? manufacturer.name : value;
  };

  // Helper function to find protocol name by ID or return the name directly
  const getProtocolName = (value) => {
    const protocol = protocols.find(p => p._id === value);
    return protocol ? protocol.name : value;
  };

  // Helper function to find size code by ID or return the code directly
  const getSizeCode = (value) => {
    const size = meterSizes.find(s => s._id === value);
    return size ? size.code : value;
  };

  const protocolTemplate = (rowData) => {
    return getProtocolName(rowData.protocol) || 'N/A';
  };

  const sizeTemplate = (rowData) => {
    return getSizeCode(rowData.size) || 'N/A';
  };

  // Template for facility name
  const facilityTemplate = (rowData) => {
    return rowData.FacilityInfo?.name || 'N/A';
  };

  const statusTemplate = (rowData) => {
    const statusColors = {
      opened: 'success',
      closed: 'danger',
      maintenance: 'warning',
      faulty: 'danger'
    };

    return (
      <span className={`badge bg-${statusColors[rowData.status] || 'secondary'}`}>
        {rowData.status}
      </span>
    );
  };

  const installationStatusTemplate = (rowData) => {
    const statusColors = {
      installed: 'success',
      'not installed': 'warning'
    };

    return (
      <span className={`badge bg-${statusColors[rowData.installationStatus] || 'secondary'}`}>
        {rowData.installationStatus}
      </span>
    );
  };

  const actionsTemplate = (rowData) => (
    <div>
      <Button
        icon="ti ti-edit"
        className="p-button-rounded p-button-info mr-2"
        onClick={() => handleModalOpen(rowData)}
        tooltip="Edit"
      />
    </div>
  );

  // --- ENHANCED IMPORT FUNCTIONALITY ---
  // Generate CSV Template for meters import with Facility and Unit fields
  const generateCSVTemplate = () => {
    const csvHeader = "meter_number,account_number,serial_number,facility_name,unit_name,manufacturer,protocol,size,initial_reading,previous_reading,current_reading,concentrator_serial_number,installation_status,status,meter_type,customer_type,bulk_meter,bulk_meter_description\n";

    // Add example rows with placeholders including facility and unit names
    const exampleRows = [
      "MTR-001,ACC-001,SN123456,Building A,Unit 101,Manufacturer A,Protocol X,25mm,100.5,95.2,100.5,CON-123,not installed,opened,smart,postpaid,false,",
      "MTR-002,ACC-002,SN789012,Building A,Unit 102,Manufacturer B,Protocol Y,20mm,85.3,80.1,85.3,CON-456,installed,opened,smart,prepaid,false,",
      "MTR-003,ACC-003,SN345678,Building B,Unit 201,Manufacturer C,Protocol Z,15mm,120.0,115.5,120.0,CON-789,not installed,opened,analog,postpaid,false,",
      "MTR-004,ACC-004,SN456789,Building B,,Manufacturer A,Protocol X,50mm,500.0,480.0,500.0,CON-890,installed,opened,smart,postpaid,true,City Council Bulk"
    ].join("\n");

    return csvHeader + exampleRows;
  };

  const handleTemplateDownload = () => {
    const csvContent = generateCSVTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'meters_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toastify('Template downloaded successfully', 'success');
  };

  const handleFileSelect = (event) => {
    const file = event.files[0];
    if (file) {
      const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const validExtensions = ['.csv', '.xls', '.xlsx'];

      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        toastify('Please select a CSV or Excel file', 'error');
        return;
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toastify('File size exceeds 10MB limit', 'error');
        return;
      }

      setUploadedFile(file);
    }
  };

  // Enhanced file upload with progress simulation
  const handleFileUpload = async () => {
    if (!uploadedFile) {
      toastify('Please select a file first', 'error');
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append('file', uploadedFile);

    setIsImporting(true);
    setImportProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await makeRequest3(`${waterMeterBaseUrl}/import`, 'POST', formDataObj);

      clearInterval(progressInterval);
      setImportProgress(100);

      if (response.success) {
        toastify('Meters imported successfully!', 'success');
        fetchMeters(pagination.currentPage, pagination.limit, searchTerm);
        setShowImportDialog(false);
        setUploadedFile(null);

        // Reset file input
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach(input => input.value = '');
      } else {
        toastify(response.error || 'Failed to import meters', 'error');
      }
    } catch (error) {
      toastify('An error occurred while importing meters', 'error');
    } finally {
      setIsImporting(false);
      setImportProgress(0);
    }
  };

  // Open the edit modal and pre-populate form data
  const handleModalOpen = (meter) => {
    if (meter) {
      setSelectedMeter(meter);

      const manufacturerId = manufacturers.find(m => m.name === meter.manufacturer || m._id === meter.manufacturer)?._id || meter.manufacturer;
      const protocolId = protocols.find(p => p.name === meter.protocol || p._id === meter.protocol)?._id || meter.protocol;
      const sizeId = meterSizes.find(s => s.code === meter.size || s._id === meter.size)?._id || meter.size;

      setFormData({
        meterNumber: meter.meterNumber || '',
        manufacturer: manufacturerId || '',
        protocol: protocolId || '',
        size: sizeId || '',
        status: meter.status || 'opened',
        initialReading: meter.initialReading || '',
        concentratorSerialNumber: meter.concentratorSerialNumber || '',
        installationStatus: meter.installationStatus || 'not installed',
      });
    }
    setShowModal(true);
  };

  // Handle meter update submission
  const handleSubmit = async () => {
    if (!formData.meterNumber.trim()) {
      toastify("Meter Number is required", "error");
      return;
    }
    if (!formData.manufacturer) {
      toastify("Manufacturer is required", "error");
      return;
    }
    if (!formData.protocol) {
      toastify("Protocol is required", "error");
      return;
    }
    if (!formData.size) {
      toastify("Size is required", "error");
      return;
    }

    const manufacturerName = getManufacturerName(formData.manufacturer);
    const protocolName = getProtocolName(formData.protocol);
    const sizeCode = getSizeCode(formData.size);

    const submitData = {
      ...formData,
      manufacturer: manufacturerName,
      protocol: protocolName,
      size: sizeCode
    };

    setIsSubmitting(true);
    try {
      const response = await makeRequest2(
        `${waterMeterBaseUrl}/${selectedMeter._id}`,
        'PUT',
        submitData
      );

      if (response.success) {
        toastify('Meter updated successfully', 'success');
        fetchMeters(pagination.currentPage, pagination.limit, searchTerm);
        setShowModal(false);
      } else {
        toastify(response.error || 'Failed to update meter', 'error');
      }
    } catch (error) {
      toastify('An error occurred while updating the meter', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle page change
  const onPageChange = (event) => {
    const newPage = event.page + 1; // PrimeReact uses 0-based index
    const newLimit = event.rows;
    fetchMeters(newPage, newLimit, searchTerm);
  };

  // Handle rows per page change
  const handleRowsChange = (e) => {
    const newLimit = e.value;
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchMeters(1, newLimit, searchTerm);
  };

  // Enhanced Import Dialog Component
  const ImportDialog = () => (
    <Dialog
      header="Import Water Meters"
      visible={showImportDialog}
      onHide={() => setShowImportDialog(false)}
      style={{ width: '60vw', maxWidth: '800px' }}
      modal
    >
      <div className="p-fluid">
        <div className="alert alert-info mb-3">
          <strong>Import Instructions:</strong>
          <ul className="mb-0 mt-2">
            <li><strong>Required fields:</strong> <code>meter_number</code>, <code>account_number</code>, <code>facility_name</code>, <code>manufacturer</code>, <code>protocol</code>, <code>size</code></li>
            <li><strong>Facility & Unit:</strong> Use exact facility and unit names as they appear in the system.</li>
            <li><strong>Optional fields:</strong> <code>unit_name</code> (leave blank for bulk meters), <code>serial_number</code>, <code>concentrator_serial_number</code></li>
            <li><strong>Meter Type:</strong> <code>smart</code> or <code>analog</code> (default: smart)</li>
            <li><strong>Customer Type:</strong> <code>postpaid</code> or <code>prepaid</code></li>
            <li><strong>Status values:</strong> <code>opened</code>, <code>closed</code>, <code>maintenance</code>, or <code>faulty</code></li>
            <li><strong>Installation status:</strong> <code>installed</code> or <code>not installed</code></li>
            <li><strong>Bulk Meters:</strong> Set <code>bulk_meter</code> to <code>true</code> and specify <code>bulk_meter_description</code> (options: City Council Bulk, Borehole Bulk, Bulk Inlet, Bulk Outlet, Bulk Borehole Outlet, Common Area)</li>
            <li>Readings should be in cubic meters (m³)</li>
          </ul>
        </div>

        <div className="mb-3">
          <button
            type="button"
            className="btn btn-link p-0 mb-2 d-flex align-items-center"
            onClick={handleTemplateDownload}
            style={{ textDecoration: 'none' }}
          >
            <i className="ti ti-download me-2"></i>
            <span>Download CSV Template</span>
          </button>
        </div>

        <div className="field mb-3">
          <h5>Upload CSV or Excel File</h5>
          <FileUpload
            mode="basic"
            accept=".csv,.xlsx,.xls"
            maxFileSize={10000000}
            chooseLabel="Choose File"
            className="mb-3"
            onSelect={handleFileSelect}
            disabled={isImporting}
          />
          {uploadedFile && (
            <div className="alert alert-success mt-2">
              <strong>Selected file:</strong> {uploadedFile.name} ({Math.round(uploadedFile.size / 1024)} KB)
            </div>
          )}
          <small className="text-muted block">
            Allowed file types: .csv, .xlsx, .xls (Max size: 10MB)
          </small>
        </div>

        {/* Progress bar */}
        {isImporting && (
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small>Importing...</small>
              <small>{importProgress}%</small>
            </div>
            <div className="progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${importProgress}%` }}
                aria-valuenow={importProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
              </div>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowImportDialog(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            label={isImporting ? "Importing..." : "Import"}
            onClick={handleFileUpload}
            disabled={!uploadedFile || isImporting}
            type="button"
            className="btn btn-primary"
          />
        </div>
      </div>
    </Dialog>
  );

  // Enhanced Pagination Display
  const PaginationInfo = () => (
    <div className="d-flex align-items-center justify-content-between mt-3">
      <div className="text-muted">
        Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalMeters)} of {pagination.totalMeters} meters
      </div>
      <div className="d-flex align-items-center">
        <span className="me-2">Rows per page:</span>
        <Dropdown
          value={pagination.limit}
          options={rowsPerPageOptions.map(opt => ({ label: opt.toString(), value: opt }))}
          onChange={handleRowsChange}
          style={{ width: '80px' }}
          disabled={loading}
        />
      </div>
    </div>
  );

  return (
    <Layout>
      <ConfirmDialog />
      <div className="page-header">
        <ul className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/core/dashboard">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">Water Meter Management</li>
          <li className="breadcrumb-item active">Meters</li>
        </ul>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-search">
                    <i className="ti ti-search"></i>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search by meter number, manufacturer, or facility"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    <Button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => navigate('/core/water_meter_management/add_new_meter')}
                    >
                      Add New Meter
                    </Button>
                    <Button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowImportDialog(true)}
                    >
                      Import Meters
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <DataTable
                value={meters}
                loading={loading}
                paginator
                rows={pagination.limit}
                rowsPerPageOptions={rowsPerPageOptions}
                first={(pagination.currentPage - 1) * pagination.limit}
                totalRecords={pagination.totalMeters}
                onPage={onPageChange}
                stripedRows
                emptyMessage="No water meters found."
                tableStyle={{ minWidth: '50rem' }}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} meters"
              >
                <Column field="meterNumber" header="Meter Number" sortable />
                <Column header="Facility" body={facilityTemplate} sortable />
                <Column header="Protocol" body={protocolTemplate} sortable />
                <Column header="Size" body={sizeTemplate} sortable />
                <Column
                  field="installationStatus"
                  header="Installation Status"
                  body={installationStatusTemplate}
                  sortable
                />
                <Column field="status" header="Status" body={statusTemplate} sortable />
                <Column field="initialReading" header="Initial Reading (m³)" sortable
                  body={(rowData) => rowData.initialReading ? `${parseFloat(rowData.initialReading).toFixed(2)} m³` : '0.00 m³'} />
                <Column field="currentReading" header="Current Reading (m³)" sortable
                  body={(rowData) => rowData.currentReading ? `${parseFloat(rowData.currentReading).toFixed(2)} m³` : '0.00 m³'} />
                <Column field="concentratorSerialNumber" header="Con Number" sortable />
                <Column header="Actions" body={actionsTemplate} />
              </DataTable>

              {/* Custom Pagination Info */}
              <PaginationInfo />
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header="Edit Meter"
        visible={showModal}
        onHide={() => setShowModal(false)}
        style={{ width: '50vw' }}
      >
        <div className="p-fluid">
          <div className="field mb-3">
            <label className="form-label">
              Meter Number<span style={{ color: "red" }}>*</span>
            </label>
            <InputText
              value={formData.meterNumber}
              onChange={(e) => setFormData({ ...formData, meterNumber: e.target.value })}
              className="form-control"
              disabled={isSubmitting}
            />
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Manufacturer<span style={{ color: "red" }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.manufacturer || ''}
              onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
              disabled={isSubmitting}
            >
              <option value="">Select Manufacturer</option>
              {manufacturers.map((manufacturer) => (
                <option key={manufacturer._id} value={manufacturer._id}>
                  {manufacturer.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Protocol<span style={{ color: "red" }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.protocol || ''}
              onChange={(e) => setFormData({ ...formData, protocol: e.target.value })}
              disabled={isSubmitting}
            >
              <option value="">Select Protocol</option>
              {protocols.map((protocol) => (
                <option key={protocol._id} value={protocol._id}>
                  {protocol.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Size<span style={{ color: "red" }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.size || ''}
              onChange={(e) => setFormData({ ...formData, size: e.target.value })}
              disabled={isSubmitting}
            >
              <option value="">Select Size</option>
              {meterSizes.map((size) => (
                <option key={size._id} value={size._id}>
                  {size.code}
                </option>
              ))}
            </select>
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Installation Status<span style={{ color: "red" }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.installationStatus || 'not installed'}
              onChange={(e) => setFormData({ ...formData, installationStatus: e.target.value })}
              disabled={isSubmitting}
            >
              <option value="installed">Installed</option>
              <option value="not installed">Not Installed</option>
            </select>
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Initial Reading (m³)<span style={{ color: "red" }}>*</span>
            </label>
            <InputText
              className="form-control"
              value={formData.initialReading}
              onChange={(e) => setFormData({ ...formData, initialReading: e.target.value })}
              disabled={isSubmitting}
              type="number"
              min="0"
              step="0.01"
            />
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Concentrator Number
            </label>
            <InputText
              className="form-control"
              value={formData.concentratorSerialNumber || ''}
              onChange={(e) => setFormData({ ...formData, concentratorSerialNumber: e.target.value })}
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className="text-end mt-4">
          <Button
            label={isSubmitting ? "Saving..." : "Save"}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="p-button-primary"
          />
        </div>
      </Dialog>
      <ImportDialog />
    </Layout>
  );
}

export default Meters;